import {
  OrderByInput,
  Product,
  useProductListOrder,
  useSetProductListOrder,
} from '@gripp/shared-logic';
import {
  Colors,
  EntityAlertListModal,
  InfiniteDataGrid,
  SelectionStatus,
} from '@gripp/shared-ui';
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { useColumnsProduct } from './useColumnsProduct';

export type SelectedProductRow = {
  id: string;
};

type ProductsListProps = {
  filter: any;
  showCheckboxColumn: boolean;
  onProductClick: (product: Product) => void;
  onRowsSelectedChange?: (rows: SelectedProductRow[]) => void;
  selectedRowIds?: string[];
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
  onItemsCountChange?: (count: number) => void;
};

export const ProductsList = ({
  filter,
  showCheckboxColumn,
  onProductClick,
  onRowsSelectedChange,
  selectedRowIds,
  selectionStatus,
  onSelectionStatusChange,
  onItemsCountChange,
}: ProductsListProps) => {
  const { containerMaxHeight, listMaxHeight } = useMaxHeight(64, 0, 90);
  const { width } = useWindowDimensions();

  const { data: productOrder } = useProductListOrder();
  const { mutateAsync: setProductOrder } = useSetProductListOrder();

  const [showAlertListModal, setShowAlertListModal] = useState(false);
  const [selectedProductIdForAlert, setSelectedProductIdForAlert] = useState<
    string | undefined
  >(undefined);

  const handleProductAlertClick = useCallback((productId: string) => {
    setSelectedProductIdForAlert(productId);
    setShowAlertListModal(true);
  }, []);

  const columns = useColumnsProduct(
    showCheckboxColumn,
    handleProductAlertClick
  );

  const updateRowsSelected = (rows: any[]) => {
    onRowsSelectedChange?.(rows.map((row) => ({ id: row.id })));
  };

  const onOrderByChange = async (orderBy: OrderByInput) => {
    await setProductOrder(orderBy);
  };

  const selectedIdsSet = useMemo(
    () => new Set(selectedRowIds ?? []),
    [selectedRowIds]
  );
  const isRowSelected = useCallback(
    (row: { id?: string }) => (row?.id ? selectedIdsSet.has(row.id) : false),
    [selectedIdsSet]
  );

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <View style={[styles.textWrapper, { maxWidth: width - 10 }]}>
        <Text style={styles.emptyTapText} numberOfLines={2}>
          Tap '+ Create Product' to add your first product.
        </Text>
      </View>
    </View>
  );

  return (
    <View
      style={[
        styles.productsContainer,
        {
          maxHeight: containerMaxHeight,
        },
      ]}
    >
      <InfiniteDataGrid
        columns={columns as any}
        query={LIST_PRODUCTS}
        dataPrefix="product.listProducts"
        modelName="product"
        orderBy={productOrder}
        pageSize={50}
        filter={filter}
        hideHeader={false}
        listMaxHeight={listMaxHeight}
        showScrollIndicator={true}
        onOrderByChange={onOrderByChange}
        textEmpty={renderEmpty()}
        showEmptyTable
        updateRowsSelected={updateRowsSelected}
        isRowSelected={isRowSelected}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={onSelectionStatusChange}
        onRowClick={onProductClick}
        exportFileName="products-export"
        onItemsCountChange={onItemsCountChange}
      />
      {showAlertListModal && selectedProductIdForAlert && (
        <EntityAlertListModal
          visible={showAlertListModal}
          entityType="product"
          entityId={selectedProductIdForAlert}
          onClose={() => {
            setShowAlertListModal(false);
            setSelectedProductIdForAlert(undefined);
          }}
        />
      )}
    </View>
  );
};

const LIST_PRODUCTS = `
  query FindProducts($filter: ListProductsFilter, $page: PageRequest, $orderBy: OrderByInput) {
    product {
      listProducts(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        limit
        offset
        items {
          id
          name
          description
          assetsCount
          filesCount
          addonsCount
          routines {
            id
            name
          }
          alerts
          productAlerts
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  productsContainer: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
  emptyContainer: {
    flexDirection: 'column',
    rowGap: 40,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    width: '100%',
  },
  textWrapper: {
    width: '100%',
  },
  emptyTapText: {
    fontWeight: '700',
    fontSize: 22,
    textAlign: 'center',
    color: Colors.role.textStrong,
  },
});
